export interface ChatbotReference {
  referenceType: 'content' | 'user' | 'topic' | 'external' | 'shortcut';
  referenceId: string;
}

export interface ReferenceContent {
  id: string;
  title: string;
  url: string;
  description?: string;
  type: 'content' | 'user';
}

export interface ChatbotMessage {
  id: string;
  threadId: ChatbotThread['id'];
  role: 'user' | 'assistant';
  content: string;
  references?: ChatbotReference[];
  placeholderText?: string;
  createdAt: string;
  updatedAt: string;
  isStreaming?: boolean;
}

export interface ChatbotThread {
  id: string;
  tenant: string;
  identity: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatbotSendMessageRequest {
  threadId?: string;
  message: string;
}

export interface MessageBatchResult {
  userMessage: ChatbotMessage;
  assistantMessage: ChatbotMessage;
}

export interface ChatbotThreadMessagesResponse {
  data: ChatbotMessage[];
}

export interface ChatbotSendMessageResponse {
  data: MessageBatchResult;
}

export interface ChatbotThreadListResponse {
  data: ChatbotThread[];
  meta: {
    page: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
  };
}

// Stream event types for SSE streaming
export type StreamStartEvent = {
  type: 'stream_start';
  data: {
    messageId: string;
    assistantId: string;
    threadId: string;
  };
};

export type MessageUpdateEvent = {
  type: 'message_update';
  data: {
    chunk: string;
  };
};

export type StreamEndEvent = {
  type: 'stream_end';
  data: {
    userMessage: ChatbotMessage;
    assistantMessage: ChatbotMessage;
  };
};

export type StreamErrorEvent = {
  type: 'error';
  data: {
    error: string;
  };
};

export type StreamPingEvent = {
  type: 'ping';
  data: Record<string, never>;
};

/**
 * Placeholder update event - sent to show placeholder text while waiting for content
 */
export type PlaceholderUpdateEvent = {
  type: 'placeholder_update';
  data: {
    text: string;
  };
};

export type MessageReferenceEvent = {
  type: 'message_reference';
  data: {
    type: 'content' | 'topic' | 'user' | 'external' | 'shortcut';
    id: string;
  };
};

export type TaskStartedEvent = {
  type: 'task_started';
  data: {
    taskId: string;
    taskText: string;
  };
};

export type TaskCompletedEvent = {
  type: 'task_completed';
  data: {
    taskId: string;
    taskText: string;
  };
};

export type NoResultsEvent = {
  type: 'no_results';
  data: Record<string, never>;
};

export type ResultsAvailableEvent = {
  type: 'results_available';
  data: Record<string, never>;
};

export type StreamEvent =
  | StreamStartEvent
  | MessageUpdateEvent
  | StreamEndEvent
  | StreamErrorEvent
  | StreamPingEvent
  | PlaceholderUpdateEvent
  | MessageReferenceEvent
  | TaskStartedEvent
  | TaskCompletedEvent
  | NoResultsEvent
  | ResultsAvailableEvent;

// Reference Card Types (from novabot backend)
export interface Avatar {
  url: string | null;
  color: string | null;
  initials: string | null;
  alt_text: string;
}

export interface Navigation {
  type: 'external' | 'deeplink' | 'internal';
  url: string;
  relative_url?: {
    web?: string;
    mobile?: string;
  };
}

export interface NavigationAction {
  type: 'navigation';
  navigation: Navigation;
}

export interface RequestAction {
  type: 'request';
  request: {
    endpoint: string;
    method: string;
    payload_string?: string;
  };
}

export type Action = NavigationAction | RequestAction;

interface BaseReferenceCard {
  type: string;
  heading: string;
  subheading: string | null;
  action: Action;
}

export type GenericCard = BaseReferenceCard & {
  type: 'generic_card';
  icon: string;
};

export type AvatarCard = BaseReferenceCard & {
  type: 'avatar_card';
  avatar: Avatar;
};

export type ExternalCard = BaseReferenceCard & {
  type: 'external_card';
  icon?: string;
  sourceIcon?: string;
};

export type ReferenceCard = GenericCard | AvatarCard | ExternalCard;

export interface ReferenceCardResponse {
  type: string;
  attributes: ReferenceCard;
}

// Type guards for reference cards
export const isGenericCard = (card: ReferenceCard): card is GenericCard =>
  card.type === 'generic_card';

export const isAvatarCard = (card: ReferenceCard): card is AvatarCard =>
  card.type === 'avatar_card';

export const isExternalCard = (card: ReferenceCard): card is ExternalCard =>
  card.type === 'external_card';

// Suggestion types
export type Suggestion = {
  label: string;
  question: string;
};

export type SuggestionsResponse = {
  data: {
    suggestions: Suggestion[];
  };
};
